import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { environment } from '@env'
import {
  CreateUserRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GetUserResponse,
  GetUsersRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  UpdatePasswordRequest,
  UserResponse,
  UserTenantResponse,
} from '@models'
import { firstValueFrom } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly userBaseUrl = `${environment.cms_latam_api.baseUrl}/user/user`
  private readonly userTenantBaseUrl = `${environment.cms_latam_api.baseUrl}/user/usertenant`

  constructor(private http: HttpClient) {}

  // ---------- Users ----------

  async getAll(params?: GetUsersRequest): Promise<GetUserResponse> {
    let httpParams = new HttpParams()

    if (params) {
      if (params.page !== undefined) httpParams = httpParams.set('page', params.page.toString())

      if (params.pageSize !== undefined) httpParams = httpParams.set('pageSize', params.pageSize.toString())

      if (params.orderBy) httpParams = httpParams.set('orderBy', params.orderBy)

      if (params.order) httpParams = httpParams.set('order', params.order)

      if (params.text) httpParams = httpParams.set('text', params.text)

      if (params.status !== undefined) httpParams = httpParams.set('status', params.status)

      if (params.tenantId !== undefined) httpParams = httpParams.set('tenantId', params.tenantId.toString())
    }

    return await firstValueFrom(this.http.get<GetUserResponse>(`${this.userBaseUrl}/paged`, { params: httpParams }))
  }

  async getById(userId: number): Promise<UserResponse> {
    return await firstValueFrom(this.http.get<UserResponse>(`${this.userBaseUrl}/${userId}`))
  }

  async createV2(payload: CreateUserRequest): Promise<number> {
    return await firstValueFrom(this.http.post<number>(`${this.userBaseUrl}/v2/user`, payload))
  }

  async updateV2(userId: number, payload: CreateUserRequest): Promise<number> {
    return await firstValueFrom(this.http.put<number>(`${this.userBaseUrl}/v2/user/${userId}`, payload))
  }

  async update(id: number, payload: UserResponse): Promise<number> {
    return await firstValueFrom(this.http.put<number>(this.userBaseUrl, payload))
  }

  async delete(id: number): Promise<void> {
    return await firstValueFrom(this.http.delete<void>(`${this.userBaseUrl}/${id}`))
  }

  // ---------- User Tenants ----------

  async getTenantsByUserId(userId: number): Promise<UserTenantResponse[]> {
    return await firstValueFrom(
      this.http.get<UserTenantResponse[]>(`${this.userTenantBaseUrl}/getByUserId`, {
        params: { userId: userId.toString() },
      })
    )
  }

  // ---------- Password ----------

  async forgotPassword(payload: ForgotPasswordRequest): Promise<void> {
    await firstValueFrom(this.http.post<ForgotPasswordResponse>(`${this.userBaseUrl}/forgot-password`, payload))
  }

  async resetPassword(payload: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    return await firstValueFrom(this.http.post<ResetPasswordResponse>(`${this.userBaseUrl}/reset-password`, payload))
  }

  async updatePassword(payload: UpdatePasswordRequest): Promise<ResetPasswordResponse> {
    return await firstValueFrom(this.http.post<ResetPasswordResponse>(`${this.userBaseUrl}/updatePassword`, payload))
  }
}
